import { omit, splitAt } from 'ramda'
import { useAutoFocus, useEffectAfterMount, useOnDestroy, useOnStopTypingState } from '@btime/react-hooks'
import React from 'react'

import InputWrapper from './InputWrapper'
import VMasker from 'vanilla-masker'

const useApplyMask = () => (value, mask) => {
  if (!!mask && !!value) {
    return VMasker.toPattern(value, mask)
  }

  return value
}

export const useBasicInputControl = ({
  value,
  masks,
  onStopTyping,
  onChange,
  replaceValue,
  limit,
  onKeyPress,
  onBlur,
  disableRemount = false
}) => {
  const [localValue, setLocalValue] = useOnStopTypingState(value, onStopTyping)
  const applyMask = useApplyMask()

  useEffectAfterMount(() => {
    if (value !== localValue && !disableRemount) {
      setLocalValue(value)
    }
  }, [value])

  const handleChange = e => {
    let newValue = !!e && !!e.target ? e.target.value : e

    if (!!replaceValue) {
      newValue = replaceValue(newValue)
    }

    if (limit && newValue.length > limit) {
      newValue = splitAt(limit, newValue)[0]
    }

    if (Array.isArray(masks) && masks.length > 0) {
      newValue = applyMask(newValue, masks[0])
    }

    setLocalValue(newValue)
    if (onChange) onChange(newValue)
  }

  const handleKey = e => {
    const key = !!e && !!e.key ? e.key : e
    const newValue = !!e && !!e.target ? e.target.value : e

    if (key === 'Enter') {
      setLocalValue(newValue)
      if (onKeyPress) onKeyPress(newValue)
    }
  }

  useOnDestroy({
    action: (newValue, currentValue) => {
      if (newValue !== currentValue) {
        onStopTyping(newValue)
      }
    },
    params: [localValue, value],
    condition: !!onStopTyping
  })

  const handleBlur = e => {
    let value = e.target.value?.trim()

    if (Array.isArray(masks) && masks.length > 0) {
      value = applyMask(value, masks[0])
    }

    return (onBlur && onBlur(value)) || (onStopTyping && onStopTyping(value))
  }

  return [localValue, { handleChange, handleBlur, handleKey }]
}

const Input = ({
  onChange,
  inputRef,
  replaceValue,
  limit,
  autoFocus,
  onStopTyping,
  value,
  initialValue,
  onKeyPress,
  onBlur,
  masks,
  ...props
}) => {
  let _inputRef = React.useRef(null)
  inputRef = inputRef || _inputRef

  useAutoFocus(inputRef, autoFocus)

  const [localValue, { handleChange, handleKey, handleBlur }] = useBasicInputControl({
    value: initialValue || value,
    masks,
    onStopTyping,
    onChange,
    limit,
    replaceValue,
    onKeyPress,
    onBlur
  })

  return (
    <InputWrapper {...props}>
      <input
        className="input"
        ref={inputRef}
        onChange={handleChange}
        onKeyPress={handleKey}
        onBlur={handleBlur}
        value={localValue || ''}
        {...omit(['leftPadding', 'leftContent', 'small'], props)}
      />
    </InputWrapper>
  )
}

export default Input
